import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { callQuery, GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE, GET_LATEST_VERSIONS_FOR_CSN, LLM_VISIT_FEEDBACK_BY_CSN, LLMVisitFeedback, NEXT_STEPS_BY_CSN, NextStep, PatientFileVersion, UNREAD_EXPLAIN_JOBS } from "../../../../amplify/appsync/query";
import { PatientVisit } from "../../../../amplify/appsync/search-patients";
import { callFhirReasonsForVisit } from "../../../../amplify/appsync/fetch-sync-fhir";

import { Badge } from "@aws-amplify/ui-react";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { callDashboardLLM, callGetDashboardLLMStatus, LLMDashboardStatus } from "../../../../amplify/appsync/dashboard-llm";
import { callWrite, EXPERIMENTAL_EXPLAIN_PATIENT_SUMMARY, EXPERIMENTAL_EXPLAIN_DDX, EXPLAIN_CURRENT_VISIT, EXPLAIN_DDX, EXPLAIN_PATIENT_SUMMARY, SCAN_MULTI, UPDATE_LAST_VIEWED } from "../../../../amplify/appsync/write";
import { useDashboardTrigger } from "../../../../context/DashboardTriggerContext";
import { SectionTrackboardNextSteps } from "../../../cohort/llm/SectionTrackboardNextSteps";
import useSetPagePatientVisit, { useGlobalOptions } from "../../../common/GlobalOptionsContext";
import { dashboardLLMCache } from "../cache";
import { AdditionalContextModal } from "../common/AdditionalContextModal";
import { ScanMultiModal } from "../common/ScanMultiModal";
import './NotesDashboard.scss';
import { SectionCurrentVisit } from "./SectionCurrentVisit";
import { SectionDDX } from "./SectionDDX";
import { SectionPatientSummary } from "./SectionPatientSummary";
import { SectionContextManagement } from "./SectionContextManagement";

import { v4 as uuidv4 } from 'uuid';
import { callLLMDashboardStreamable } from "../../../../amplify/api/llm-chat";
import { getWSService } from "../../../../amplify/api/llm-web-socket";
import { getFileFromKey } from "../common/utils";
import IssueTracker from "../../../cohort/llm/ActionableIntelligenceTracker";
import { isLobby } from "../utils";
import { SectionTrackboardVerticalVideo } from "../../../cohort/llm/SectionTrackboardVerticalVideo";

export const JOB_CONFIG = [
    {
        key: "current_visit_presentation",
        file: EXPLAIN_CURRENT_VISIT,
    },
    {
        key: "patient_summary",
        file: EXPLAIN_PATIENT_SUMMARY,
        experimentalFile: EXPERIMENTAL_EXPLAIN_PATIENT_SUMMARY,
    },
    {
        key: "ddx",
        file: EXPLAIN_DDX,
        experimentalFile: EXPERIMENTAL_EXPLAIN_DDX,
    },
];

let socketConnection: any;
let sessionId: string;

export const NotesDashboard = (props: {
    user: any,
    ws: string,
    tenant: string,
    patientVisit?: PatientVisit,
    unreadFileVersionMap?: Record<string, PatientFileVersion>,
    setUnreadFileVersionMap?: (map: Record<string, PatientFileVersion>) => void,
}) => {
    let { internalCSN } = useParams();
    const { getDerivedRole } = useGlobalOptions();

    const [statuses, setStatuses] = useState<Record<string, LLMDashboardStatus>>({});
    const patientVisit = props.patientVisit;
    useSetPagePatientVisit(patientVisit);

    const [feedbacks, setFeedbacks] = useState<LLMVisitFeedback[]>([]);
    const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
    const [existingNextSteps, setExistingNextSteps] = useState<NextStep[]>([]);
    const [reasonsForVisit, setReasonsForVisit] = useState<string>();
    const [isExperimental, setIsExperimental] = useState<boolean>(false);

    const { triggerAction, setTriggerAction } = useDashboardTrigger();

    //
    // State variables for the scan multi popup
    //
    const [isScanMultiModalOpen, setIsScanMultiModalOpen] = useState(false);
    const [latestFileVersions, setLatestFileVersions] = useState<Record<string, PatientFileVersion>>();
    const [selectedScanMultiVersion, setSelectedScanMultiVersion] = useState<any>();
    const [selectedInitialSection, setSelectedInitialSection] = useState<string>();
    const [selectedTriggeringEvents, setSelectedTriggeringEvents] = useState<string[]>();

    //
    // State variables for the pre-computed data
    //
    const [currentVisitPresentation, setCurrentVisitPresentation] = useState<any>();
    const [patientSummary, setPatientSummary] = useState<any>();
    const [ddx, setDdx] = useState<any>();

    //
    // State variables that hold which sub-section is currently active
    //
    const [ddxActive, setDdxActive] = useState<number>(0);

    // 
    // Preparing environment
    // 
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [regenerateTarget, setRegenerateTarget] = useState<string | undefined>();
    const [isWebSocketConnecting, setIsWebSocketConnecting] = useState<any>(false);
    const regenerateKeyRef = useRef<any>();

    const [isLoadingScanMulti, setIsLoadingScanMulti] = useState<boolean>(false);
    const [isLoadingCurrentVisitPresentation, setIsLoadingCurrentVisitPresentation] = useState<boolean>(false);
    const [isLoadingPatientSummary, setIsLoadingPatientSummary] = useState<boolean>(false);
    const [isLoadingDdx, setIsLoadingDdx] = useState<boolean>(false);

    // Update the ref whenever regenerateTarget changes
    useEffect(() => {
        let config = null;
        for (let i = 0; i < JOB_CONFIG.length; i++) {
            const target = isExperimental ? "experimental_" + regenerateTarget : regenerateTarget;
            const fileToCheck = isExperimental ? JOB_CONFIG[i].experimentalFile : JOB_CONFIG[i].file;
            if (target && fileToCheck !== target) {
                continue;
            }
            config = JOB_CONFIG[i];
        }
        regenerateKeyRef.current = config?.key;
    }, [regenerateTarget, isExperimental]);

    // Optimization 1: Use refs to avoid recreating objects on every render
    const sectionToSetIsLoading: any = {
        scan_multi: setIsLoadingScanMulti,
        current_visit_presentation: setIsLoadingCurrentVisitPresentation,
        patient_summary: setIsLoadingPatientSummary,
        ddx: setIsLoadingDdx,
    };

    const sectionToGetter = useRef<any>({
        current_visit_presentation: currentVisitPresentation,
        patient_summary: patientSummary,
        ddx: ddx,
    });

    const sectionToSetter = useRef<any>({
        current_visit_presentation: setCurrentVisitPresentation,
        patient_summary: setPatientSummary,
        ddx: setDdx,
    });

    useEffect(() => {
        sectionToGetter.current = {
            current_visit_presentation: currentVisitPresentation,
            patient_summary: patientSummary,
            ddx: ddx,
        };
    }, [currentVisitPresentation, patientSummary, ddx]);

    const loadPrecomputedExplanations = useCallback(async (patient: PatientVisit) => {

        const sections = JOB_CONFIG;

        for (let section of sections) {
            sectionToSetIsLoading[section.key]?.(true);
            const fileType = (isExperimental ? section.experimentalFile : section.file) ?? "";
            const cachedCurrentVisit = await dashboardLLMCache.get(fileType, patient, props.tenant);
            if (cachedCurrentVisit) {
                sectionToSetter.current[section.key]?.(cachedCurrentVisit);
            } else {
                sectionToSetter.current[section.key]?.();
            }
            sectionToSetIsLoading[section.key]?.(false);
        }
    }, [props.tenant, isExperimental]);

    useEffect(() => {
        if (!patientVisit) return;

        loadPrecomputedExplanations(patientVisit);

        callGetDashboardLLMStatus(patientVisit.csn).then((statuses) => {
            setStatuses(statuses);
        });

        callQuery({
            operation: LLM_VISIT_FEEDBACK_BY_CSN,
            csn: patientVisit.csn,
        }).then(feedbacks => {
            setFeedbacks(feedbacks);
        });

        callQuery({
            operation: GET_LATEST_VERSIONS_FOR_CSN,
            csn: patientVisit.csn,
        }).then((latestVersions: PatientFileVersion[]) => {
            const fileTypeToVersion: any = {};
            for (let latestVersion of latestVersions) {
                fileTypeToVersion[latestVersion.file_type] = latestVersion;
            }
            setLatestFileVersions(fileTypeToVersion);
        });

        callQuery({
            operation: NEXT_STEPS_BY_CSN,
            csn: patientVisit.csn,
        }).then((nextSteps: NextStep[]) => {
            for (let nextStep of nextSteps) {
                if (!nextStep.updated_date) {
                    nextStep.updated_date = nextStep.creation_date
                }
            }
            setExistingNextSteps(nextSteps);
        });

        if (patientVisit.fhir_id) {
            callFhirReasonsForVisit({
                csn: patientVisit.csn,
                fhir_id: patientVisit.fhir_id,
            }).then((output: any) => {
                setReasonsForVisit(output ? JSON.stringify(output) : undefined);
            });
        }
    }, [patientVisit, loadPrecomputedExplanations]);

    useEffect(() => {
        if (!patientVisit) return;
        if (!props.unreadFileVersionMap) return;

        callQuery({
            operation: UNREAD_EXPLAIN_JOBS,
            csn: patientVisit.csn,
        }).then((unreadFileVersions: PatientFileVersion[]) => {
            const fileVersionMap: Record<string, PatientFileVersion> = {};
            for (let fv of unreadFileVersions) {
                fileVersionMap[fv.file_type] = fv;
            }
            props.setUnreadFileVersionMap?.(fileVersionMap);

            const jobs = JOB_CONFIG.map(config => isExperimental ? config.experimentalFile : config.file);
            callWrite({
                operation: UPDATE_LAST_VIEWED,
                csn: patientVisit.csn,
                jobs: jobs,
            }).then((result: any) => {
                console.log("Completed UPDATE_LAST_VIEWED");
            });
        });
    }, [patientVisit, isExperimental]);

    // Store accumulated text in a ref so we don't trigger extra re-renders
    const accumulatedTextRef = useRef("");
    const lastSeqRef = useRef(0);

    // Optimization 4: Use useCallback for event handlers to prevent re-renders
    const handleChatUpdate = useCallback((response: any) => {
        if (
            response &&
            typeof response === "object" &&
            !Array.isArray(response) &&
            (response["role"] === "COMPLETED" || response["role"] === "COMPLETED_WITH_ERROR")
        ) {
            console.log("End of stream", response, regenerateKeyRef.current);

            // End of stream cleanup
            if (props.patientVisit && regenerateKeyRef.current) {
                console.log("Refreshing the stream");
                callQuery({
                    operation: GET_LATEST_VERSIONS_FOR_CSN,
                    csn: props.patientVisit.csn,
                }).then((latestVersions: PatientFileVersion[]) => {
                    const fileTypeToVersion: any = {};
                    for (let latestVersion of latestVersions) {
                        fileTypeToVersion[latestVersion.file_type] = latestVersion;
                    }
                    setLatestFileVersions(fileTypeToVersion);
                });
                dashboardLLMCache.get(getFileFromKey(JOB_CONFIG, regenerateKeyRef.current, isExperimental), props.patientVisit, props.tenant).then((cachedCurrentVisit) => {
                    console.log("Updating loading status...");
                    sectionToSetter.current[regenerateKeyRef.current]?.(cachedCurrentVisit);
                    sectionToSetIsLoading[regenerateKeyRef.current](false);
                });
                callGetDashboardLLMStatus(props.patientVisit.csn).then((statuses) => {
                    setStatuses(statuses);
                });
            }
        }
        // Oct 2025: Disable streaming because time-to-first token is high for Azure OpenAI models
        //           which leads to a degraded/non-existent stream. Re-enable once we move back to 
        //           native OpenAI or Claude models.
        //
        // else if (response && typeof response === "object" && "seq" in response && "delta" in response) {
        //     const { seq, delta } = response;

        //     console.log("Received chunk", response);

        //     // Optional: Check for skipped or out-of-order chunks
        //     if (seq !== lastSeqRef.current + 1) {
        //         console.warn(`⚠ Out of order chunk: expected ${lastSeqRef.current + 1} but got ${seq}`);
        //         // Optionally handle gap recovery here
        //     }

        //     // Append the new delta to accumulated text
        //     accumulatedTextRef.current += delta;
        //     lastSeqRef.current = seq;

        //     // Push the updated text into your UI
        //     if (regenerateKeyRef.current) {
        //         const key: any = regenerateKeyRef.current;
        //         sectionToSetIsLoading[key](false);
        //         try {
        //             sectionToSetter.current[key](JSON.parse(jsonrepair(accumulatedTextRef.current)));
        //         } catch {
        //             console.warn(`Could not parse the following accumulated text: ${accumulatedTextRef.current}`);
        //             sectionToSetter.current[key]({});
        //         }
        //     }
        // }
        // else if (regenerateKeyRef.current) {
        //     console.log("Fallback", response);
        //     // Fallback: if the backend sends a plain string (legacy mode)
        //     const key: any = regenerateKeyRef.current;
        //     sectionToSetIsLoading[key](false);
        //     sectionToSetter.current[key](response);
        // }
    }, [regenerateTarget]);

    const handleWebSocketConnected = useCallback(() => {
        setIsWebSocketConnecting(false);
    }, []);

    const connect = () => {
        accumulatedTextRef.current = "";
        lastSeqRef.current = 0;

        setIsWebSocketConnecting(true);
        sessionId = uuidv4();
        socketConnection = getWSService(props.ws, sessionId, handleWebSocketConnected);
        if (socketConnection.sessionId) {
            // This is because getWSService may actually return a previous open web socket with an older connection ID
            sessionId = socketConnection.sessionId;
        }

        if (!socketConnection.hasListener(handleChatUpdate)) {
            socketConnection.addMessageListener(handleChatUpdate);
        }
    }

    const regenerate = useCallback(async (shouldSyncData: boolean, overrideTarget?: string) => {

        let config = null;
        accumulatedTextRef.current = "";
        lastSeqRef.current = 0;

        let target = overrideTarget;
        if (!target) {
            target = regenerateTarget;
        }
        target = isExperimental ? "experimental_" + target : target;

        for (let i = 0; i < JOB_CONFIG.length; i++) {
            const fileToCheck = isExperimental ? JOB_CONFIG[i].experimentalFile : JOB_CONFIG[i].file;

            if (target && fileToCheck !== target) {
                continue;
            }
            if (JOB_CONFIG[i].file.startsWith("scan_")) {
                continue;
            }
            config = JOB_CONFIG[i];
        }
        if (patientVisit) {
            setIsRegenerating(true);

            connect();
            if (config) {
                sectionToSetIsLoading[config.key]?.(true);
            }

            if (shouldSyncData) {
                const scanMultiResponse = await callDashboardLLM({
                    "operation": SCAN_MULTI,
                    "mrn": patientVisit.mrn,
                    "csn": patientVisit.csn,
                });
            }
            if (config) {
                const operationFile = isExperimental ? config.experimentalFile : config.file;
                await callLLMDashboardStreamable(sessionId, {
                    operation: operationFile,
                    mrn: patientVisit.mrn,
                    csn: patientVisit.csn,
                    report_date_str: new Date().toISOString().split('.')[0] + 'Z',
                });
            }
            setIsRegenerating(false);
        }
    }, [patientVisit, isExperimental]);

    useEffect(() => {
        if (triggerAction && triggerAction.name === 'notes') {
            regenerate(true);
            // Important: Reset the trigger action to avoid re-triggering
            setTriggerAction(null);
        }
    }, [triggerAction, regenerate, setTriggerAction]);

    /**
     * Called when the user wants to see the data snapshot for the LLM section
     */
    const handleShowScanMultiModalForSection = useCallback((patientFileVersion?: PatientFileVersion) => {
        if (patientFileVersion) {
            callQuery({
                operation: GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE,
                csn: patientFileVersion.csn,
                file_type: 'scan_multi',
            })
            .then((versions: PatientFileVersion[]) => {
                // Find previous version
                const sortedVersions = [...versions].sort((a, b) => new Date(a.version_date).getTime() - new Date(b.version_date).getTime());
                const selectedIndex = sortedVersions.findIndex(v => v.version_id === patientFileVersion.source_version_id);
                const previousVersion = selectedIndex > 0 ? sortedVersions[selectedIndex - 1] : null;

                setSelectedScanMultiVersion({
                    origVersion: previousVersion ? previousVersion.source_version_id : undefined,
                    newVersion: patientFileVersion.source_version_id,
                });
            });
            setSelectedTriggeringEvents(patientFileVersion.source_changed_keys);
            setIsScanMultiModalOpen(true);
        }
    }, []);

    /**
     * Called when the user selects another version of the LLM section
     */
    const handleFileVersionChange = useCallback((fileType: string, fileVersion: PatientFileVersion, previousFileVersion: PatientFileVersion | null) => {
        const updatedFileVersions = {
            ...latestFileVersions,
            [fileType]: {
                ...fileVersion
            },
        };
        setSelectedScanMultiVersion({
            origVersion: previousFileVersion ? previousFileVersion.source_version_id : undefined,
            newVersion: fileVersion.source_version_id,
        })
        setLatestFileVersions(updatedFileVersions);
    }, [latestFileVersions]);

    // Optimization 5: Memoize the regenerate button to prevent unnecessary re-renders
    const regenerateButton = useMemo(() => {
        if (!patientVisit) return null;
        
        if (isRegenerating) {
            return (
                <Badge>
                    <FontAwesomeIcon className="notes-metadata" icon={faRefresh} spin /> Regenerating...
                </Badge>
            );
        } else {
            return (
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {setRegenerateTarget(undefined); setShowContextMenu(true)}}
                    disabled={isRegenerating}
                >
                    <FontAwesomeIcon icon={faRefresh} /> Regenerate All
                </Button>
            );
        }
    }, [patientVisit, isRegenerating]);

    return (
        <Container className="notes-dashboard">
            <Row className="mb-3">
                <Col>
                    <span className="gradient-text" style={{fontSize: '1.25rem'}}>Workup</span>
                </Col>
                <Col className="text-right">
                    {
                        getDerivedRole(props.user) === "ADMIN" && (
                            <Form className="mt-2 text-muted notes-metadata">
                                <Form.Check
                                    type="switch"
                                    label="Experimental"
                                    color="secondary"
                                    inline
                                    checked={isExperimental}
                                    onChange={(e) => setIsExperimental(e.target.checked)}
                                />
                            </Form>
                        )
                    }
                    {/* {regenerateButton} */}
                    {patientVisit && (
                        <AdditionalContextModal
                            regenerate={regenerate}
                            showModal={showContextMenu}
                            setShowModal={setShowContextMenu}
                            patientVisit={patientVisit}
                            target={regenerateTarget}
                            tenant={props.tenant}
                        />
                    )}
                </Col>
            </Row>

            {
                patientVisit && isLobby(patientVisit) && (
                    <SectionTrackboardVerticalVideo user={props.user} className="mb-2" isLoadingDdx={isLoadingDdx} ddx={ddx} patientVisit={patientVisit} feedbacks={feedbacks} setFeedbacks={setFeedbacks} statuses={statuses} latestFileVersions={latestFileVersions} handleShowScanMultiModal={handleShowScanMultiModalForSection} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} unreadFileVersionMap={props.unreadFileVersionMap} tenant={props.tenant} />
                )
            }

            <SectionPatientSummary
                className="dashboard-section mb-3"
                isLoadingPatientSummary={isLoadingPatientSummary}
                patientSummary={patientSummary}
                patientVisit={patientVisit}
                feedbacks={feedbacks}
                setFeedbacks={setFeedbacks}
                statuses={statuses}
                setRegenerateTarget={setRegenerateTarget}
                setShowContextMenu={setShowContextMenu}
                latestFileVersions={latestFileVersions}
                handleShowScanMultiModal={handleShowScanMultiModalForSection}
                sectionToSetter={sectionToSetter.current}
                handleFileVersionChange={handleFileVersionChange}
                unreadFileVersionMap={props.unreadFileVersionMap}
                user={props.user}
                tenant={props.tenant}
            />
            <SectionCurrentVisit
                className="dashboard-section mb-3"
                isLoadingCurrentVisitPresentation={isLoadingCurrentVisitPresentation}
                currentVisitPresentation={currentVisitPresentation}
                patientVisit={patientVisit}
                feedbacks={feedbacks}
                setFeedbacks={setFeedbacks}
                statuses={statuses}
                reasonsForVisit={reasonsForVisit}
                setRegenerateTarget={setRegenerateTarget}
                setShowContextMenu={setShowContextMenu}
                latestFileVersions={latestFileVersions}
                handleShowScanMultiModal={handleShowScanMultiModalForSection}
                sectionToSetter={sectionToSetter.current}
                handleFileVersionChange={handleFileVersionChange}
                unreadFileVersionMap={props.unreadFileVersionMap}
                user={props.user}
                tenant={props.tenant}
            />
            {patientVisit && (
                <SectionContextManagement
                    className="dashboard-section mb-3"
                    patientVisit={patientVisit}
                    tenant={props.tenant}
                />
            )}
            <SectionDDX
                user={props.user}
                tenant={props.tenant}
                className="dashboard-section mb-3"
                isLoadingDdx={isLoadingDdx}
                ddx={ddx}
                ddxActive={ddxActive}
                setDdxActive={setDdxActive}
                patientVisit={patientVisit}
                feedbacks={feedbacks}
                setFeedbacks={setFeedbacks}
                statuses={statuses}
                setRegenerateTarget={setRegenerateTarget}
                setShowContextMenu={setShowContextMenu}
                latestFileVersions={latestFileVersions}
                handleShowScanMultiModal={handleShowScanMultiModalForSection}
                sectionToSetter={sectionToSetter.current}
                handleFileVersionChange={handleFileVersionChange}
                unreadFileVersionMap={props.unreadFileVersionMap}
            />
            <SectionTrackboardNextSteps
                className="dashboard-section mb-2"
                isLoadingDdx={isLoadingDdx}
                ddx={ddx}
                tenant={props.tenant}
                patientVisit={props.patientVisit}
                feedbacks={feedbacks}
                setFeedbacks={setFeedbacks}
                statuses={statuses}
                setRegenerateTarget={setRegenerateTarget}
                setShowContextMenu={setShowContextMenu}
                existingNextSteps={existingNextSteps}
                setExistingNextSteps={setExistingNextSteps}
                latestFileVersions={latestFileVersions}
                handleShowScanMultiModal={handleShowScanMultiModalForSection}
                sectionToSetter={sectionToSetter.current}
                handleFileVersionChange={handleFileVersionChange}
                unreadFileVersionMap={props.unreadFileVersionMap}
            />
            {patientVisit && (
                <ScanMultiModal 
                    patient={patientVisit} 
                    isOpen={isScanMultiModalOpen} 
                    onClose={() => {setIsScanMultiModalOpen(false)}} 
                    versionInfo={selectedScanMultiVersion} 
                    initialSection={selectedInitialSection} 
                    triggeringEvents={selectedTriggeringEvents} 
                />
            )}
        </Container>
    );
};
